import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { cadStr } from "@/lib/pricing";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { PlanChangePreview } from "@/components/merchant/billing/PlanChangePreview";
import { fmtDate } from "@/components/merchant/billing/KeepPriceNotice";

const RANK = { basic: 0, starter: 1, growth: 2 };

function priceLabel(plan, cadence) {
  if (plan.monthly_cad === "0.00") return "CA$0.00 — no subscription fee";
  return cadence === "annual" ? `${cadStr(plan.annual_cad)}/year` : `${cadStr(plan.monthly_cad)}/month`;
}

function ConfirmDialog({ target, onClose, onConfirm, busy, periodEnd }) {
  const [ready, setReady] = useState(false);
  if (!target) return null;
  const up = target.kind === "upgrade";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="plan-change-dialog">
        <DialogHeader>
          <DialogTitle>{up ? `Upgrade to ${target.plan.name}?` : `Move to ${target.plan.name}?`}</DialogTitle>
          <DialogDescription className="text-sm text-slate-600 pt-1" data-testid="plan-change-dialog-copy">
            {up
              ? `Starts now. You're charged the prorated difference today, then ${priceLabel(target.plan, target.cadence)}. New verified sales use the ${target.plan.fee_pct}% or ${cadStr(target.plan.fee_minimum_cad)} fee from now on.`
              : `Takes effect at the end of your paid period${periodEnd ? ` (${fmtDate(periodEnd)})` : ""}. Nothing is refunded and your storefronts, campaigns and history stay. After that, sales use the ${target.plan.fee_pct}% or ${cadStr(target.plan.fee_minimum_cad)} fee.`}
          </DialogDescription>
        </DialogHeader>
        <PlanChangePreview plan={target.plan.id} cadence={target.cadence} onReady={setReady} />
        <DialogFooter className="flex-row justify-end gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="plan-change-dialog-cancel" className="rounded-full text-xs">Not now</Button>
          <Button onClick={onConfirm} disabled={busy || !ready} data-testid="plan-change-dialog-confirm" className="rounded-full text-xs">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}{up ? "Confirm upgrade" : "Schedule change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ChangePlanCard({ catalog, status, currentPlan, onChanged }) {
  const curCadence = status.cadence || "monthly";
  const [cadence, setCadence] = useState(curCadence);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const effCadence = (p) => (RANK[p.id] < RANK[currentPlan] || p.id === "basic" ? curCadence : cadence);
  const isUp = (p) => RANK[p.id] > RANK[currentPlan]
    || (p.id === currentPlan && effCadence(p) === "annual" && curCadence === "monthly");
  const options = catalog.plans.filter((p) => p.id !== currentPlan || (cadence === "annual" && curCadence === "monthly"));

  const pick = (plan) => setTarget({ plan, cadence: effCadence(plan), kind: isUp(plan) ? "upgrade" : "downgrade" });
  const confirm = async () => {
    try {
      setBusy(true);
      const { data } = await api.post("/billing/sg/change-plan", { plan: target.plan.id, cadence: target.cadence });
      toast.success(data?.kind === "upgrade" ? `You're on ${target.plan.name} now.` : `${target.plan.name} starts at the end of your paid period.`);
      setTarget(null);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change plan.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="plan-change-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Change plan</div>
        <div className="inline-flex p-1 rounded-full bg-slate-100 text-xs font-semibold" data-testid="plan-change-cadence">
          {["monthly", "annual"].map((c) => (
            <button key={c} onClick={() => setCadence(c)} data-testid={`plan-change-cadence-${c}`}
                    className={`px-3 py-1 rounded-full transition-colors ${cadence === c ? "bg-slate-900 text-white" : "text-slate-600"}`}>
              {c === "monthly" ? "Monthly" : "Annual · 2 months free"}
            </button>
          ))}
        </div>
      </div>
      <div className={`mt-4 grid gap-3 ${options.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
        {options.map((p) => {
          const up = isUp(p);
          return (
            <button key={p.id} onClick={() => pick(p)} data-testid={`plan-change-option-${p.id}`}
                    className="text-left rounded-xl border border-slate-200 p-4 hover:border-cyan-400 transition-colors">
              <div className="flex items-center justify-between">
                <span className="font-display text-lg text-slate-900">{p.name}</span>
                {up ? <ArrowUpRight className="w-4 h-4 text-emerald-600" /> : <ArrowDownRight className="w-4 h-4 text-amber-600" />}
              </div>
              <div className="text-xs text-slate-600 mt-1">{priceLabel(p, effCadence(p))}</div>
              <div className="text-xs text-slate-500 mt-1">{p.fee_pct}% or {cadStr(p.fee_minimum_cad)} per verified sale</div>
              <div className={`text-[11px] font-semibold mt-2 ${up ? "text-emerald-700" : "text-amber-700"}`}>
                {up ? "Starts now · prorated" : "At the end of your paid period"}
              </div>
            </button>
          );
        })}
      </div>
      <ConfirmDialog target={target} busy={busy} periodEnd={status.current_period_end}
                     onClose={() => setTarget(null)} onConfirm={confirm} />
    </div>
  );
}
